import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { 
  Truck, User, Package, Clock, MapPin, RefreshCw, X, AlertTriangle,
  CheckCircle, Search, Filter, Phone, ArrowRight, XCircle
} from 'lucide-react';

/**
 * ========================================
 * ADMIN DELIVERY MANAGEMENT
 * ========================================
 * View assigned deliveries and unassign drivers if needed
 */

const AdminDeliveryManagement = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('accepted'); // 'all', 'accepted', 'collected', 'in_transit'
  const [unassigning, setUnassigning] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  // Fetch assignments with driver and order details
  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('driver_assignments')
        .select(`
          id,
          driver_id,
          order_id,
          status,
          gatepass_status,
          assigned_at,
          gatepass_issued_at,
          gatepass_verified_at,
          created_at,
          orders (
            id,
            order_number,
            status,
            delivery_address_id,
            delivery_address_snapshot,
            addresses (
              area,
              physical_address
            )
          )
        `)
        .order('created_at', { ascending: false });

      // Apply status filter
      if (filter !== 'all') {
        query = query.eq('status', filter);
      } else {
        // Exclude delivered/cancelled for 'all'
        query = query.in('status', ['accepted', 'collected', 'in_transit']);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch driver details separately (driver_id = user_id, not drivers.id)
      const driverIds = [...new Set((data || []).map(a => a.driver_id).filter(Boolean))];
      const driversMap = {};
      if (driverIds.length > 0) {
        const { data: driversData } = await supabase
          .from('drivers')
          .select('user_id, name, phone, vehicle_type, vehicle_registration')
          .in('user_id', driverIds);
        (driversData || []).forEach(d => driversMap[d.user_id] = d);
      }

      // Attach driver data to each assignment
      const dataWithDrivers = (data || []).map(a => ({
        ...a,
        drivers: driversMap[a.driver_id] || null
      }));

      // Also fetch delivery box info for each order
      const assignmentsWithBoxes = await Promise.all(
        (dataWithDrivers || []).map(async (assignment) => {
          if (assignment.order_id) {
            const { data: boxData } = await supabase
              .from('delivery_boxes')
              .select('box_number')
              .eq('order_id', assignment.order_id)
              .single();
            
            return {
              ...assignment,
              box_number: boxData?.box_number || null
            };
          }
          return assignment;
        })
      );

      setAssignments(assignmentsWithBoxes);
    } catch (err) {
      console.error('Error fetching assignments:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchAssignments, 30000);
    return () => clearInterval(interval);
  }, [fetchAssignments]);

  // Filter assignments by search query
  const filteredAssignments = assignments.filter(a => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      a.orders?.order_number?.toLowerCase().includes(query) ||
      a.drivers?.name?.toLowerCase().includes(query) ||
      a.drivers?.phone?.includes(query) ||
      a.box_number?.toLowerCase().includes(query) ||
      (a.orders?.delivery_address_snapshot?.area || a.orders?.addresses?.area)?.toLowerCase().includes(query)
    );
  });

  // Handle unassign click
  const handleUnassignClick = (assignment) => {
    setSelectedAssignment(assignment);
    setShowConfirmModal(true);
  };

  // Confirm unassign
  const handleConfirmUnassign = async () => {
    if (!selectedAssignment) return;

    setUnassigning(selectedAssignment.id);
    try {
      // 1. Delete the driver assignment
      const { error: deleteError } = await supabase
        .from('driver_assignments')
        .delete()
        .eq('id', selectedAssignment.id);

      if (deleteError) throw deleteError;

      // 2. Reset the order status back to ready_for_dispatch so it becomes available again
      if (selectedAssignment.order_id) {
        const { error: orderError } = await supabase
          .from('orders')
          .update({ 
            status: 'ready_for_dispatch',
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedAssignment.order_id)
          .in('status', ['out_for_delivery', 'in_transit', 'dispatched']);

        if (orderError) {
          console.error('Error resetting order status:', orderError);
        }
      }

      // 3. Log the unassignment in order_status_history
      await supabase
        .from('order_status_history')
        .insert({
          order_id: selectedAssignment.order_id,
          status: 'driver_unassigned',
          notes: `Driver ${selectedAssignment.drivers?.name || 'Unknown'} was unassigned by admin. Order made available for reassignment.`
        });

      // Refresh the list
      await fetchAssignments();
      setShowConfirmModal(false);
      setSelectedAssignment(null);
    } catch (err) {
      console.error('Error unassigning driver:', err);
      alert('Failed to unassign driver: ' + err.message);
    } finally {
      setUnassigning(null);
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ${diffMins % 60}m ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      accepted: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Accepted' },
      collected: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Collected' },
      in_transit: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'In Transit' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', label: 'Delivered' }
    };
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Get gatepass status badge
  const getGatepassBadge = (status) => {
    if (status === 'verified') {
      return (
        <span className="flex items-center gap-1 text-xs text-green-600">
          <CheckCircle size={12} />
          Verified
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-xs text-amber-600">
        <Clock size={12} />
        Pending
      </span>
    );
  };

  // Check if assignment is taking too long (over 30 mins without gatepass verification)
  const isDelayed = (assignment) => {
    if (assignment.status !== 'accepted') return false;
    if (assignment.gatepass_status === 'verified') return false;
    
    const acceptedAt = new Date(assignment.assigned_at || assignment.created_at);
    const now = new Date();
    const diffMins = (now - acceptedAt) / 60000;
    
    return diffMins > 30;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="text-blue-600" />
            Delivery Management
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor assigned deliveries and unassign drivers if needed
          </p>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by order #, driver, phone, box, or area..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter size={20} className="text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Active</option>
                <option value="accepted">Accepted (Waiting)</option>
                <option value="collected">Collected</option>
                <option value="in_transit">In Transit</option>
              </select>
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchAssignments}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-sm text-gray-600">Total Active</div>
            <div className="text-2xl font-bold text-gray-900">{assignments.length}</div>
          </div>
          <div className="bg-yellow-50 rounded-xl shadow-sm p-4 border border-yellow-200">
            <div className="text-sm text-yellow-700">Waiting at Gate</div>
            <div className="text-2xl font-bold text-yellow-800">
              {assignments.filter(a => a.status === 'accepted' && a.gatepass_status !== 'verified').length}
            </div>
          </div>
          <div className="bg-red-50 rounded-xl shadow-sm p-4 border border-red-200">
            <div className="text-sm text-red-700">Delayed (&gt;30min)</div>
            <div className="text-2xl font-bold text-red-800">
              {assignments.filter(isDelayed).length}
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl shadow-sm p-4 border border-blue-200">
            <div className="text-sm text-blue-700">In Progress</div>
            <div className="text-2xl font-bold text-blue-800">
              {assignments.filter(a => a.status === 'collected' || a.status === 'in_transit').length}
            </div>
          </div>
        </div>

        {/* Assignments List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Truck className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">No assignments found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAssignments.map((assignment) => (
              <div
                key={assignment.id}
                className={`bg-white rounded-xl shadow-sm overflow-hidden ${
                  isDelayed(assignment) ? 'border-2 border-red-300' : ''
                }`}
              >
                {/* Delayed Warning Banner */}
                {isDelayed(assignment) && (
                  <div className="bg-red-50 px-4 py-2 flex items-center gap-2 text-red-700 border-b border-red-200">
                    <AlertTriangle size={16} />
                    <span className="text-sm font-medium">
                      Driver has not arrived at warehouse - Accepted {formatTimeAgo(assignment.assigned_at || assignment.created_at)}
                    </span>
                  </div>
                )}

                <div className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Order Info */}
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Package className="text-blue-600" size={24} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-900">
                            #{assignment.orders?.order_number || 'N/A'}
                          </span>
                          {getStatusBadge(assignment.status)}
                          {getGatepassBadge(assignment.gatepass_status)}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          {assignment.box_number && (
                            <span className="flex items-center gap-1">
                              <Package size={14} />
                              Box {assignment.box_number}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {assignment.orders?.delivery_address_snapshot?.area || assignment.orders?.addresses?.area || 'Unknown'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Driver Info */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="text-gray-600" size={20} />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {assignment.drivers?.name || 'Unknown Driver'}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Phone size={12} />
                            {assignment.drivers?.phone || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Time & Actions */}
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Accepted</div>
                        <div className="font-medium text-gray-900">
                          {formatTimeAgo(assignment.assigned_at || assignment.created_at)}
                        </div>
                      </div>

                      {/* Unassign Button - Show for all active statuses */}
                      <button
                        onClick={() => handleUnassignClick(assignment)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-medium text-sm"
                      >
                        <XCircle size={18} />
                        Unassign
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Confirm Unassign Modal */}
        {showConfirmModal && selectedAssignment && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="text-red-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Unassign Driver</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Order:</span>
                    <span className="ml-2 font-medium">#{selectedAssignment.orders?.order_number}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Box:</span>
                    <span className="ml-2 font-medium">{selectedAssignment.box_number || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Driver:</span>
                    <span className="ml-2 font-medium">{selectedAssignment.drivers?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <span className="ml-2 font-medium">{selectedAssignment.drivers?.phone}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                The driver assignment will be removed and the order status will be reset to <span className="font-semibold">Ready for Dispatch</span>, 
                making it available for other drivers to accept.
                {(selectedAssignment.status === 'collected' || selectedAssignment.status === 'in_transit') && (
                  <span className="block mt-2 text-red-600 font-medium">
                    ⚠️ This driver has already {selectedAssignment.status === 'in_transit' ? 'started delivering' : 'collected'} this order. 
                    Make sure to coordinate with the driver before unassigning.
                  </span>
                )}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setSelectedAssignment(null);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUnassign}
                  disabled={unassigning}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {unassigning ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Unassigning...
                    </>
                  ) : (
                    <>
                      <XCircle size={18} />
                      Unassign Driver
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDeliveryManagement;
